import { db, type LiftXDatabase } from '../db';
import { buildBackup, verifyBackup } from './exportBackup';
import { createBackupRecordsRepository } from '../repositories/backupRecordsRepository';
import { createIntegrityRepository } from '../repositories/integrityRepository';

/**
 * Automatic backups. Master Phase 9, Phase 0 §11.3: "written to IndexedDB on
 * a schedule with grandfather-father-son retention... manual and pre* kinds
 * never expiring."
 *
 * **These protect against a bad edit or app bug, not against the failure
 * mode LiftX is most exposed to.** An automatic backup lives in the same
 * origin storage that Safari eviction or "clear site data" would wipe
 * (R-2/R-3) — only an exported *file*, already built since Phase 3, defends
 * against that. Storing full media blobs here would cost real space for zero
 * benefit against exactly the risk this table can't insure against, so these
 * are JSON-payload-only — the risk register's own "metadata-only backup
 * option" (R-9) mitigation, applied automatically rather than only on
 * request.
 *
 * "Check on app open, write if due" is not a compromise — it is the only
 * reliably reachable execution moment on an iOS Safari PWA, which has no
 * Background Sync support. Fire-and-forget, exactly like `mediaRepository
 * .sweepOrphans()`'s existing call site: startup housekeeping, never a
 * precondition for the app to be usable.
 */

interface Schedule {
  kind: string;
  intervalMs: number;
  retain: number;
}

const SCHEDULES: readonly Schedule[] = [
  { kind: 'daily', intervalMs: 24 * 60 * 60 * 1000, retain: 14 },
  { kind: 'weekly', intervalMs: 7 * 24 * 60 * 60 * 1000, retain: 8 },
  { kind: 'monthly', intervalMs: 30 * 24 * 60 * 60 * 1000, retain: 12 },
];

export async function runScheduledBackupIfDue(database: LiftXDatabase = db): Promise<void> {
  const backupRecords = createBackupRecordsRepository(database);
  const integrity = createIntegrityRepository(database);

  try {
    for (const { kind, intervalMs, retain } of SCHEDULES) {
      const latest = await backupRecords.latestOf(kind);
      if (latest !== undefined && Date.now() - latest.createdAt < intervalMs) continue;

      const envelope = await buildBackup(database);
      const ok = (await verifyBackup(envelope)) && (await integrity.check()).ok;

      await backupRecords.record({
        kind,
        envelope: ok ? envelope : null,
        integrityOk: ok,
        note: ok ? null : 'Automatic backup failed post-write verification.',
        checksum: envelope.checksum,
        byteCount: JSON.stringify(envelope).length,
        fileName: kind,
        schemaVersion: envelope.schemaVersion,
        createdAt: Date.now(),
      });

      if (ok) await backupRecords.pruneKind(kind, retain);
    }
  } catch {
    // Startup housekeeping must never block first paint or the rest of the
    // app — matches `ensurePersistentStorage()`'s own "never throws" rule.
    // A failed check simply tries again next time the app opens.
  }
}
